import React, { useContext, useEffect } from 'react';
import { Button, message } from 'antd';
import BillingStripeFormWrapper from 'lib.components/src/components/BillingStripeForm';
import { StripeContext } from 'lib.context/src/StripeContext';
import Loader from 'lib.components/src/components/Loader';
import { BillingSectionType } from 'lib.context/src/models/billing-section-types';
import { UserBillingPageContext } from 'lib.context/src/UserBillingPageContext';
import { DrawerCustom } from 'lib.components';
import { useBreakpoints } from 'lib.hooks/src';

interface AddPaymentStripeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  onSuccessCallback?: () => void;
  saveBtnText?: string;
  title?: string;
  isShowCancelBtn?: boolean;
}

const AddPaymentStripeDrawer: React.FC<AddPaymentStripeDrawerProps> = ({
  isOpen,
  onClose,
  isLoading,
  onSuccessCallback,
  saveBtnText,
  title,
  isShowCancelBtn = true,
}) => {
  const { md: isDesktop } = useBreakpoints();
  const { loading, intent, setIntent, apiKey, onAddPaymentClick, onOpenStripeForm } =
    useContext(StripeContext);
  const { clickedSectionType, setActualSectionType } = useContext(UserBillingPageContext);

  const onModalClose = () => {
    onClose();
    setIntent(null);
  };

  const onSubmit = async () => {
    try {
      await onAddPaymentClick();
      if (onSuccessCallback) {
        return onSuccessCallback();
      }
      if (clickedSectionType) {
        return setActualSectionType(BillingSectionType.NONE);
      }

      onClose();
    } catch (error) {
      console.error('Add payment failed:', error);
      message.error('Failed to add payment method. Please try again.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      onOpenStripeForm();
    }
  }, [isOpen]);

  return (
    <DrawerCustom
      title={title || 'Add new card'}
      open={isOpen}
      onClose={onModalClose}
      onCancel={onModalClose}
      onOk={onSubmit}
      resetButtonText="Cancel"
      applyButtonText={saveBtnText || 'Save'}
      isLoading={loading || isLoading}
      isShowResetBtn={isShowCancelBtn}
      width={450}
      footer={
        isDesktop ? (
          <div className="drawer-footer flex gap-12">
            {isShowCancelBtn ? (
              <Button size="large" block type="text" onClick={onModalClose}>
                Cancel
              </Button>
            ) : null}
            <Button
              size="large"
              block
              type="primary"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onSubmit}
              loading={loading || isLoading}
            >
              {saveBtnText || 'Save'}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="drawer-content" data-test-id="add-payment-drawer">
        {loading && <Loader />}
        {intent ? <BillingStripeFormWrapper intent={intent} apiKey={apiKey} /> : null}
      </div>
    </DrawerCustom>
  );
};

export default AddPaymentStripeDrawer;
